import { SortingState } from "@tanstack/react-table";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { setSearch } from "store/features/search/slice";
import { ObjectWithAnyValues } from "utils/types";

export function useQuerystringParams<FilterType extends ObjectWithAnyValues>(
	setFilters: (value: any) => void,
	sorting: SortingState,
	setSorting: (value: any) => void
) {
	const location = useLocation();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const clearQueryString = (obj: FilterType) => {
		Object.keys(obj).forEach((key) => {
			if (
				obj[key] === undefined ||
				obj[key] === "" ||
				(Array.isArray(obj[key]) && obj[key].length === 0)
			) {
				delete obj[key];
			}
		});
		setFilters(obj);
		return obj;
	};

	const updateSort = (sortQuery: string | undefined) => {
		if (!sortQuery || typeof sortQuery !== "string") return;

		const [id, desc] = sortQuery.split(",");
		const parsedSort = { id, desc: false };

		if (desc && desc.trim() === "desc") {
			parsedSort.desc = true;
		}

		if (
			sorting[0]?.id !== parsedSort.id ||
			(sorting[0]?.id === parsedSort.id && sorting[0]?.desc !== parsedSort.desc)
		) {
			setSorting([parsedSort]);
		}
	};

	const updateUrl = (newFilters: FilterType) => {
		updateSort(newFilters.sort);
		const search = new URLSearchParams(clearQueryString(newFilters));
		dispatch(setSearch(search));
		navigate(`${location.pathname}?${search}`, { replace: true });
	};

	return { updateUrl };
}

export function useQuerystringFilters(filters: ObjectWithAnyValues) {
	const [searchParams] = useSearchParams();
	const { search } = useSelector((state: any) => state.search);
	const urlFilters = {
		size: 10,
		page: 1
	} as ObjectWithAnyValues;

	const getUrlFilters = () => {
		let params = searchParams;
		if (searchParams.size === 0) {
			if (
				search instanceof URLSearchParams
					? search.size === 0
					: Object.keys(search).length === 0
			) {
				return {};
			}
			params = search;
		}

		params.forEach((value, key) => {
			if (typeof filters[key] === "object") {
				urlFilters[key] = params.get(key)?.split(",");
			} else if (key === "size" || key === "page") {
				urlFilters[key] = parseInt(value, 10);
			} else {
				urlFilters[key] = value;
			}
		});
		return urlFilters;
	};

	return { getUrlFilters };
}
